import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { creditosLivres } from "@shared/credito-saldo";
import { useModulos } from "@/hooks/use-modulos";
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  Package,
  Boxes,
  TicketPlus,
  LogOut,
  Menu,
  UserCircle,
  HelpCircle,
  DollarSign,
  Car,
  ShieldCheck,
  Settings2,
  ClipboardList,
  CreditCard,
  TrendingUp,
  Download,
  LayoutGrid,
  BarChart2,
  ArrowRightLeft,
  PieChart,
  Receipt,
  Smartphone,
  FlaskConical,
  BookOpen,
  GraduationCap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";
import { useMobile } from "@/hooks/use-mobile";
import { LogoAvatar } from "@/components/LogoUpload";
import { ROLE_LABELS } from "@/lib/constants";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { useCreditosSaldo } from "@/hooks/use-data";
import { InstallApkDialog } from "@/components/InstallApkDialog";

export function Sidebar() {
  const [location] = useLocation();
  const { logout, profile } = useAuth();
  const isMobile = useMobile();
  const [open, setOpen] = useState(false);
  const [installDialogOpen, setInstallDialogOpen] = useState(false);

  const { modulos } = useModulos();
  const roles = profile?.roles || [];
  const isAdmin = roles.includes("admin");
  const isOwner = roles.includes("owner");
  const isParceiro = roles.includes("parceiro_negocios");
  const empresaTipo = profile?.empresa?.tipo ?? "";

  const creditosAtivos = isParceiro && modulos.creditosPrePagos;
  const { data: saldo } = useCreditosSaldo(
    creditosAtivos ? profile?.empresaId : undefined,
  );
  const totalDisponivel = saldo?.reduce((s, b) => s + creditosLivres(b), 0) ?? 0;
  const totalDevendo = saldo?.reduce((s, b) => s + (b.devendo ?? 0), 0) ?? 0;
  const saldoLiquido = totalDisponivel - totalDevendo;
  const canSeeFinanceiro = (isOwner || isAdmin) && empresaTipo === "OWNER";

  const navItems = [
    { label: "Painel", icon: LayoutDashboard, href: "/dashboard" },
    { label: "Tutoriais", icon: GraduationCap, href: "/tutoriais" },
    { label: isParceiro ? "Minha empresa" : "Empresas", icon: Building2, href: "/empresas" },
    { label: "Usuários", icon: Users, href: "/usuarios" },
    { label: "Laudos", icon: FileText, href: "/laudos" },
    ...((isOwner || isAdmin) && empresaTipo !== "PARCEIRO_DE_NEGOCIOS"
      ? [{ label: "Laudos / Parceiro", icon: PieChart, href: "/laudos/por-parceiro" }]
      : []),
    { label: "Veículos", icon: Car, href: "/veiculos" },
    { label: "Peças", icon: Package, href: "/pecas" },
    ...((isOwner || isAdmin || isParceiro) &&
    empresaTipo === "PARCEIRO_DE_NEGOCIOS" &&
    modulos.creditosPrePagos
      ? [{ label: "Créditos", icon: CreditCard, href: "/creditos" }]
      : []),
    ...(isOwner || isAdmin
      ? [{ label: "Perguntas", icon: HelpCircle, href: "/pecas/perguntas" }]
      : []),
    ...(isOwner || isAdmin
      ? [{ label: "Lotes", icon: Boxes, href: "/lotes" }]
      : []),
    ...(isAdmin || isOwner || roles.includes("rt")
      ? [{ label: "Convites", icon: TicketPlus, href: "/convites" }]
      : []),
    ...(canSeeFinanceiro
      ? [{ label: "Financeiro", icon: DollarSign, href: "/financeiro" }]
      : []),
    // Vistoriadora: pagar suas próprias faturas ao Owner
    ...(empresaTipo === "VISTORIADORA" && (isOwner || isAdmin)
      ? [{ label: "Minhas Faturas", icon: FileText, href: "/minhas-faturas" }]
      : []),
    // Vistoriadora: emitir faturas para Parceiros (some com o módulo de Créditos:
    // com créditos pré-pagos, o parceiro já paga na compra, não há fatura a emitir)
    ...(empresaTipo === "VISTORIADORA" && (isOwner || isAdmin) && !modulos.creditosPrePagos
      ? [
          {
            label: "Fat. Parceiros",
            icon: ClipboardList,
            href: "/faturas-parceiro",
          },
        ]
      : []),
    // Parceiro: visualizar faturas recebidas da Vistoriadora (mesma regra acima)
    ...(empresaTipo === "PARCEIRO_DE_NEGOCIOS" && isParceiro && !modulos.creditosPrePagos
      ? [{ label: "Faturas", icon: FileText, href: "/faturas-parceiro" }]
      : []),
    // Vistoriadora: emitir NF-e das compras de crédito (módulo precisa estar ativo)
    ...(empresaTipo === "VISTORIADORA" &&
    (isOwner || isAdmin) &&
    modulos.creditosPrePagos &&
    modulos.nfeAtivado
      ? [{ label: "Nota Fiscal", icon: Receipt, href: "/nota-fiscal" }]
      : []),
    ...((isOwner || isAdmin || roles.includes("rt")) && empresaTipo === "VISTORIADORA"
      ? [{ label: "Relatórios", icon: BarChart2, href: "/relatorios" }]
      : []),
    // Vistoriadora em Modo Teste: painel pra simular compra/fatura do parceiro sem Asaas
    ...((isOwner || isAdmin) &&
    empresaTipo === "VISTORIADORA" &&
    (profile?.empresa as { modoTeste?: boolean } | undefined)?.modoTeste
      ? [{ label: "Painel de Testes", icon: FlaskConical, href: "/painel-testes" }]
      : []),
    ...((isOwner || isAdmin) && modulos.creditosPrePagos
      ? [{ label: "Dashboard de Vendas", icon: TrendingUp, href: "/vendas" }]
      : []),
    ...(isOwner || isAdmin
      ? [{ label: "Auditoria", icon: ShieldCheck, href: "/auditoria" }]
      : []),
    ...(isOwner
      ? [
          {
            label: "Módulos",
            icon: LayoutGrid,
            href: "/admin/modulos",
          },
          { label: "Repasses Asaas", icon: ArrowRightLeft, href: "/owner/repasses" },
          {
            label: "Gerenciar APKs",
            icon: Smartphone,
            href: "/owner/apks",
          },
          {
            label: "Gerenciar Tutoriais",
            icon: BookOpen,
            href: "/owner/tutoriais",
          },
        ]
      : []),
    ...(empresaTipo === "VISTORIADORA" && isAdmin
      ? [{ label: "Repasses Asaas", icon: ArrowRightLeft, href: "/repasses" }]
      : []),
    ...((isAdmin || isOwner || roles.includes("rt")) &&
    empresaTipo !== "PARCEIRO_DE_NEGOCIOS" &&
    modulos.laudosGratisPorCliente > 0
      ? [{ label: "Solicitações", icon: ClipboardList, href: "/solicitacoes" }]
      : []),
    // Parceiro: acompanha suas próprias solicitações de autorização (somente leitura)
    ...(isParceiro &&
    empresaTipo === "PARCEIRO_DE_NEGOCIOS" &&
    modulos.laudosGratisPorCliente > 0
      ? [{ label: "Solicitações", icon: ClipboardList, href: "/solicitacoes" }]
      : []),
    ...(isAdmin
      ? [
          {
            label: "Ficha Técnica",
            icon: ClipboardList,
            href: "/ficha-tecnica",
          },
        ]
      : []),
    ...(isAdmin
      ? [{ label: "Configurações", icon: Settings2, href: "/configuracoes" }]
      : []),
  ];

  const displayRole =
    roles.length > 0
      ? roles
          .map((e) => {
            return ROLE_LABELS[e];
          })
          .join(", ")
      : "usuário";

  const NavContent = () => (
    <div className="flex flex-col h-full bg-slate-900 text-white">
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/20 shrink-0">
            <span className="font-display font-bold text-xl text-white">L</span>
          </div>
          <div className="flex-1">
            <h1 className="font-display font-bold text-xl tracking-tight">
              LaudoLegal
            </h1>
            <p className="text-xs text-white/50">Gestão Técnica</p>
          </div>
          <NotificationBell />
        </div>
      </div>

      <div className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link key={item.href} href={item.href} onClick={() => setOpen(false)}>
            <div
              data-testid={`nav-${item.href.replace("/", "")}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer",
                location === item.href
                  ? "bg-primary text-white shadow-md shadow-primary/25"
                  : "text-white/70 hover:text-white hover:bg-white/10",
              )}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </div>
          </Link>
        ))}
      </div>

      <div className="p-4 border-t border-white/10">
        <div className="h-10">
          <Button
            size="sm"
            variant="outline"
            className="text-primary font-bold gap-2"
            data-testid="button-cta-download"
            onClick={() => setInstallDialogOpen(true)}
          >
            <Download className="w-5 h-5" />
            Baixar o App
          </Button>
        </div>
        <Link href="/perfil" onClick={() => setOpen(false)}>
          <div
            className="flex items-center gap-3 mb-4 px-2 cursor-pointer rounded-lg py-2 hover:bg-white/10 transition-colors"
            data-testid="nav-perfil"
          >
            {profile?.empresa ? (
              <LogoAvatar
                empresaId={profile.empresa.id}
                logoUrl={profile.empresa.logoUrl}
                fallback={profile?.nome?.substring(0, 2).toUpperCase() || "?"}
                size="xs"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white">
                {profile?.nome?.substring(0, 2).toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-medium truncate">
                {profile?.nome || "Carregando..."}
              </p>
              <p className="text-xs text-white/50 truncate uppercase">
                {displayRole}
              </p>
              {creditosAtivos && (
                <div className="flex items-center gap-1 mt-0.5">
                  <CreditCard className="w-3 h-3 text-amber-400" />
                  <span className="text-xs text-amber-400 font-semibold">
                    {saldoLiquido < 0
                      ? `Devendo ${Math.abs(saldoLiquido)}`
                      : `${saldoLiquido} crédito${saldoLiquido !== 1 ? "s" : ""} livre${saldoLiquido !== 1 ? "s" : ""}`}
                  </span>
                </div>
              )}
            </div>
            <UserCircle className="w-4 h-4 text-white/40" />
          </div>
        </Link>
        <Button
          variant="outline"
          className="w-full border-white/20 text-white hover:bg-white/10 hover:text-white hover:border-white/30 justify-start gap-2 bg-transparent"
          onClick={() => logout()}
          data-testid="button-logout"
        >
          <LogOut className="w-4 h-4" />
          Sair
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className="flex items-center justify-between p-4 bg-slate-900 text-white lg:hidden">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-primary to-accent flex items-center justify-center">
              <span className="font-display font-bold text-white">L</span>
            </div>
            <span className="font-display font-bold text-lg">LaudoLegal</span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 border-r-0">
                <NavContent />
              </SheetContent>
            </Sheet>
          </div>
        </div>
        <InstallApkDialog open={installDialogOpen} onOpenChange={setInstallDialogOpen} />
      </>
    );
  }

  return (
    <>
      <div className="hidden lg:flex w-72 flex-col fixed inset-y-0 z-50">
        <NavContent />
      </div>
      <InstallApkDialog open={installDialogOpen} onOpenChange={setInstallDialogOpen} />
    </>
  );
}
